import { firstQueryValue } from '@omnea-app/fe-routing/routeParams'
import { ROUTE_REGISTRY, type RouteDefinition } from './routeRegistry'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'
import type { ParsedUrlQuery } from 'querystring'

type Registry = typeof ROUTE_REGISTRY

/** A route addressable without caller-supplied values, so a link cannot name one that needs them. */
export type NavigableRouteKey = {
  [Key in RouteKey]: Registry[Key] extends { params: readonly string[] } ? never : Key
}[RouteKey]

/** An optional catch-all matches its own parent path, so the route is entered with the segment absent. */
const OPTIONAL_CATCH_ALL = /\/\[\[\.\.\..+\]\]$/

const DYNAMIC_SEGMENT = /^\[(.+)\]$/

const paramOf = (patternPart: string): string | null => DYNAMIC_SEGMENT.exec(patternPart)?.[1] ?? null

const readParam = (key: RouteKey, param: string, query: ParsedUrlQuery): string => {
  const value = firstQueryValue(query, param)
  if (value == null) throw new Error(`Route ${key} needs ${param}, which the address being left does not carry`)

  return value
}

/** The URL for a route, taking the values it needs from the address the caller is on. */
export const hrefForFrom = (key: RouteKey, query: ParsedUrlQuery): string => {
  const definition: RouteDefinition = ROUTE_REGISTRY[key]
  if (definition.href != null) return definition.href

  const { segment } = definition
  const parts = definition.pattern.replace(OPTIONAL_CATCH_ALL, '').split('/')
  const base = parts
    .map((part) => {
      const param = paramOf(part)
      if (param == null) return part

      return segment?.queryParam === param ? segment.value : readParam(key, param, query)
    })
    .join('/')

  const patternCarriesSegment = parts.some((part) => paramOf(part) === segment?.queryParam)

  return segment != null && patternCarriesSegment === false ? `${base}/${segment.value}` : base
}

export const hrefFor = (key: NavigableRouteKey): string => hrefForFrom(key, {})
